import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminProfile,
  retrieveStudentDetails,
  updateAdminProfile,
  updateSuperAdminProfile,
} from "../../services/admin.services";
import { changeUserPassword } from "../../services/user.services";
import { useNavigate } from "react-router-dom";
import { setUserDetails } from "../../state/reducers/userSlice";
import { getsuperAdminProfile } from "../../services/superadmin.services";
import { setSelectedAdmin } from "../../state/reducers/adminSlice";

const EditProfile = () => {
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const user = useSelector((state) => state.user.userDetails);
  const role = useSelector((state) => state.user.role);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    id: user?.admin_id || user?.superadmin_id,
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    profile_picture_url: user?.profile_picture_url,
    profileImage: "",
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ""));
  };

  const validateName = (name) => {
    return name.trim().length >= 2 && /^[a-zA-Z\s]+$/.test(name.trim());
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const validateImageFile = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return "Please select a valid image file (JPEG, PNG, GIF, or WebP)";
    }

    if (file.size > maxSize) {
      return "Image size should be less than 5MB";
    }

    return null;
  };

  // Profile form validation
  const validateProfileForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    } else if (!validateName(formData.first_name)) {
      newErrors.first_name =
        "First name must be at least 2 characters and contain only letters";
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    } else if (!validateName(formData.last_name)) {
      newErrors.last_name =
        "Last name must be at least 2 characters and contain only letters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (formData.profileImage instanceof File) {
      const imageError = validateImageFile(formData.profileImage);
      if (imageError) {
        newErrors.profileImage = imageError;
      }
    }

    return newErrors;
  };

  // Password form validation
  const validatePasswordForm = () => {
    const newErrors = {};

    if (!passwordData.oldPassword) {
      newErrors.oldPassword = "Current password is required";
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (!validatePassword(passwordData.newPassword)) {
      newErrors.newPassword =
        "Password must be at least 8 characters with uppercase, lowercase, and number";
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (
      passwordData.oldPassword &&
      passwordData.newPassword &&
      passwordData.oldPassword === passwordData.newPassword
    ) {
      newErrors.newPassword =
        "New password must be different from current password";
    }

    return newErrors;
  };

  // Extract clean error message from API response
  const extractErrorMessage = (error, defaultMessage) => {
    if (error.response?.data) {
      const data = error.response.data;

      // Try different possible error message fields
      if (typeof data === "string") {
        return data;
      }

      if (data.message) {
        return data.message;
      }

      if (data.error) {
        return data.error;
      }

      if (data.errors && Array.isArray(data.errors)) {
        return data.errors.join(", ");
      }

      if (data.errors && typeof data.errors === "object") {
        return Object.values(data.errors).flat().join(", ");
      }
    }

    return defaultMessage;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
    setProfileSuccess("");
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });

    // Clear specific field error when user starts typing
    if (passwordErrors[name]) {
      setPasswordErrors({ ...passwordErrors, [name]: "" });
    }
    setPasswordSuccess("");
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageError = validateImageFile(file);
      if (imageError) {
        setErrors({ ...errors, profileImage: imageError });
        e.target.value = ""; // Clear the file input
        return;
      }

      setFormData({
        ...formData,
        profileImage: file,
      });

      // Clear image error if exists
      if (errors.profileImage) {
        setErrors({ ...errors, profileImage: "" });
      }
    }
  };

  const getProfile = async (user_id) => {
    try {
      if (role === "admin") {
        const res = await getAdminProfile(user_id);
        dispatch(setUserDetails(res.data));
        dispatch(setSelectedAdmin(res.data.admin_id));
      } else if (role === "superadmin") {
        const res = await getsuperAdminProfile(user_id);
        const data = {
          ...res.data,
          user_id: res.data.superadmin_id,
        };
        dispatch(setUserDetails(data));
      } else {
        const res = await retrieveStudentDetails(user_id);
        dispatch(setUserDetails(res.data));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handlePasswordSave = async () => {
    setPasswordErrors({});
    setPasswordSuccess("");

    const validationErrors = validatePasswordForm();
    if (Object.keys(validationErrors).length > 0) {
      setPasswordErrors(validationErrors);
      return;
    }

    setIsPasswordSubmitting(true);

    try {
      const passwordUpdateData = {
        old_password: passwordData.oldPassword,
        new_password: passwordData.newPassword,
      };

      await changeUserPassword(passwordUpdateData);

      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setPasswordSuccess("Password changed successfully!");
    } catch (error) {
      console.error("Error changing password:", error);
      const errorMessage = extractErrorMessage(
        error,
        "Failed to change password. Please try again."
      );
      setPasswordErrors({ general: errorMessage });
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    setErrors({});
    setProfileSuccess("");

    const validationErrors = validateProfileForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    const updatedFormData = new FormData();
    updatedFormData.append("superadmin_id", formData.id);
    updatedFormData.append("admin_id", formData.id);
    updatedFormData.append("first_name", formData.first_name.trim());
    updatedFormData.append("last_name", formData.last_name.trim());
    updatedFormData.append("email", formData.email.trim());
    updatedFormData.append("phone", formData.phone.trim());
    updatedFormData.append(
      "username",
      `${formData.first_name} ${formData.last_name}`
    );
    updatedFormData.append(
      "profile_picture_url",
      formData.profile_picture_url || ""
    );

    if (formData.profileImage instanceof File) {
      updatedFormData.append("profile_picture", formData.profileImage);
    }

    try {
      let response;
      if (role === "superadmin") {
        response = await updateSuperAdminProfile(formData.id, updatedFormData);
      } else {
        response = await updateAdminProfile(formData.id, updatedFormData);
      }

      setFormData((prevState) => ({
        ...prevState,
        profile_picture_url: response.data?.profile_picture_url,
        profileImage: "", // Clear the file input after successful upload
      }));
      getProfile(formData.id);
      setProfileSuccess("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorMessage = extractErrorMessage(
        error,
        "Failed to update profile. Please try again."
      );
      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (role === "admin" || role === "superadmin") {
      setFormData({
        id: user?.admin_id || user?.superadmin_id,
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        phone: user?.phone || "",
        profile_picture_url: user?.profile_picture_url,
        profileImage: "",
      });
    } else if (role === "student") {
      setFormData({
        id: user?.student_id,
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        phone: user?.phone || "",
        profile_picture_url: user?.profile_picture_url,
        profileImage: "",
      });
    }
  }, [role, user]);

  return (
    <div
      className={`flex-1 bg-white ${
        isSideBarOpen ? "lg:ml-80" : "lg:ml-20"
      } min-h-screen p-4 sm:p-6 mx-auto`}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
      >
        Back
      </button>

      <h1 className="text-xl sm:text-2xl font-bold mb-6">Edit Profile</h1>

      <div className="flex flex-col lg:flex-row lg:justify-between w-full gap-6">
        {/* Profile Form */}
        <div className="w-full lg:w-1/2 mx-auto mt-8 bg-white p-4 sm:p-6 rounded shadow">
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-6">
            {formData.first_name} {formData.last_name}
          </h1>

          {/* Success message */}
          {profileSuccess && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {profileSuccess}
            </div>
          )}

          {/* General error message */}
          {errors.general && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {errors.general}
            </div>
          )}

          <form>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profile Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                    errors.profileImage ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.profileImage && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.profileImage}
                  </p>
                )}
                {(formData.profileImage || formData.profile_picture_url) &&
                  !errors.profileImage && (
                    <p className="text-sm text-gray-500 mt-2">
                      Selected File:{" "}
                      {formData.profileImage instanceof File ? (
                        formData.profileImage.name
                      ) : (
                        <a
                          href={formData.profile_picture_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 underline"
                        >
                          Current profile image
                        </a>
                      )}
                    </p>
                  )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                    errors.first_name ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.first_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.first_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                    errors.last_name ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.last_name && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.last_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 bg-gray-100 ${
                    errors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  readOnly
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-200 ${
                    errors.phone ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                )}
              </div>
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleUpdate}
                disabled={isSubmitting}
                className="w-full sm:w-auto bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? "SAVING..." : "SAVE"}
              </button>
            </div>
          </form>
        </div>

        {/* Password Form */}
        <div className="w-full lg:w-1/2 mx-auto mt-8 bg-white p-4 sm:p-6 rounded shadow">
          <h2 className="text-lg sm:text-xl font-bold mb-4">
            Change your password
          </h2>

          {/* Success message */}
          {passwordSuccess && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {passwordSuccess}
            </div>
          )}

          {/* General password error */}
          {passwordErrors.general && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {passwordErrors.general}
            </div>
          )}

          <form>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.oldPassword ? "text" : "password"}
                  name="oldPassword"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange}
                  className={`w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring focus:ring-blue-200 ${
                    passwordErrors.oldPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("oldPassword")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.oldPassword ? (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464m1.414 1.414L8.464 8.464m5.656 5.656l1.415 1.415m-1.415-1.415l1.415 1.415M14.828 14.828L16.242 16.242"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {passwordErrors.oldPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {passwordErrors.oldPassword}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.newPassword ? "text" : "password"}
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className={`w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring focus:ring-blue-200 ${
                    passwordErrors.newPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("newPassword")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.newPassword ? (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464m1.414 1.414L8.464 8.464m5.656 5.656l1.415 1.415m-1.415-1.415l1.415 1.415M14.828 14.828L16.242 16.242"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {passwordErrors.newPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {passwordErrors.newPassword}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Must be at least 8 characters with uppercase, lowercase, and
                number
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password *
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className={`w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring focus:ring-blue-200 ${
                    passwordErrors.confirmPassword
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords.confirmPassword ? (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464m1.414 1.414L8.464 8.464m5.656 5.656l1.415 1.415m-1.415-1.415l1.415 1.415M14.828 14.828L16.242 16.242"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {passwordErrors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">
                  {passwordErrors.confirmPassword}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handlePasswordSave}
              disabled={isPasswordSubmitting}
              className="w-full sm:w-auto bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
            >
              {isPasswordSubmitting ? "SAVING..." : "SAVE"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
